import { Box, CircularProgress, Typography } from '@material-ui/core'
import { InventoryPresenter, StockState } from 'components/domain/stockPricing'
import { LocalArticle, RefreshStockIcon, SealedIcon, SinglesStockIcon } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { localDB } from 'config/localforage'
import { PublishChangesResult } from 'lib/api'
import { useConfirm, useDialog, useErrorHandler } from 'lib/hooks'
import { useFetchUserStock, useHydrateArticles } from 'lib/hooks/useFetchUserStock'
import { useLocalForageState } from 'lib/hooks/useLocalForageState'
import { useStockStore } from 'lib/hooks/useStockStore'
import { isArticleEdited } from 'lib/utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect } from 'react-router-dom'
import {
  Article,
  createInventoryArticle,
  IdGame,
  InventoryArticle,
  InventoryFetchState,
  PublishChangesResponse,
  StockTab,
  STOCK_TYPES,
  User,
} from 'shared'
import { PublishChangesDialog } from './PublishChangesDialog'

export const useHandleSettingsChange = () => {
  const { handle } = useErrorHandler()
  const { setUser } = useUser()

  //change this to do naive update and then validate

  const handleChange = useCallback(
    async (apiRequest: () => Promise<Partial<User>>): Promise<void> => {
      try {
        const response = await apiRequest()
        setUser((user) => ({ ...user!, ...response }))
      } catch (err: any) {
        handle(err)
      }
    },
    [setUser]
  )
  return handleChange
}

const tabs = ['unedited', 'failedAutopricing', 'edited'] as const

// another game's stock from a paginated refresh, saved before its card data is available
type RawStockState = {
  stock: Article['attributes'][]
  sealedStock: Article['attributes'][]
  fetchedAt: string
  fetchedAtTimestamp: number
}

export const StockPricingPresenter = (): JSX.Element => {
  const { activeGame, user, setUser } = useUser()
  const { handle } = useErrorHandler()
  const { t } = useTranslation()

  const [setFetchingDialogOpen, FetchingDialog] = useDialog()
  const [publishChangesDialogOpen, setPublishChangesDialogOpen] = useState(false)

  const [setPublishErrorsDialogOpen, PublishErrorsDialog] = useDialog()
  const [publishFailures, setPublishFailures] = useState<PublishChangesResponse>()

  const fetchUserStock = useFetchUserStock()
  const hydrateArticles = useHydrateArticles()
  const setActiveStockType = useStockStore((state) => state.setActiveStockType)

  const [fetchState, setFetchState] = useState(InventoryFetchState.FETCHING)

  const [inventoryState, setInventoryState, loadingFromLocal] = useLocalForageState<StockState>(
    { stock: [], sealedStock: [], fetchedAt: new Date().toLocaleString('sk-SK') },
    `${user?.cardmarketUsername}-stock`,
    {
      duckFunction: (value) => value.stock.find((article) => article.card._id),
      hydrate: (value) => ({
        ...value,
        stock: value.stock.map((article) => createInventoryArticle(article)),
        sealedStock: value.sealedStock.map((article) => createInventoryArticle(article)),
      }),
    }
  )
  const activeStockType = useStockStore((state) => state.activeStockType)

  const rawStockKey = (idGame: IdGame) => `${idGame}-${user?.cardmarketUsername}-rawStock`

  useEffect(() => {
    // paginated stock pulls every game at once, so only fetch when the user clicks refresh
    if (user?.featureFlags?.paginatedStock === true) {
      if (!loadingFromLocal) applyRawStock()
      return
    }
    if (!loadingFromLocal && !inventoryState.stock.length) {
      fetchStock()
    }
  }, [loadingFromLocal])

  // a refresh on another game saved this game's stock raw; hydrate it now that its card data is loaded
  const applyRawStock = async () => {
    const key = rawStockKey(activeGame.idGame)
    const raw = await localDB.getItem<RawStockState>(key)
    if (!raw) return
    await localDB.removeItem(key)

    const isNewer = raw.fetchedAtTimestamp > (inventoryState.fetchedAtTimestamp || 0)
    // never drop unpublished edits; the user can still refresh this game explicitly
    const hasEdits = [...inventoryState.stock, ...inventoryState.sealedStock].some(isArticleEdited)
    if (!isNewer || hasEdits) return

    setInventoryState({
      ...raw,
      stock: hydrateArticles(raw.stock),
      sealedStock: hydrateArticles(raw.sealedStock),
    })
  }

  useEffect(() => {
    if (loadingFromLocal === true) setFetchState(InventoryFetchState.FETCHING)
    if (loadingFromLocal === false) setFetchState(InventoryFetchState.IDLE)
  }, [loadingFromLocal])

  const fetchStock = useCallback(async () => {
    setFetchState(InventoryFetchState.FETCHING)
    setFetchingDialogOpen(true)
    try {
      if (!user?.cardmarketUsername) throw "You don't have a Cardmarket account connected."
      const {
        stock: stockData,
        sealedStock: sealedStockData,
        otherGames,
      } = await fetchUserStock(activeGame.idGame)

      const fetchedAt = new Date().toLocaleString('sk-SK')
      const fetchedAtTimestamp = Date.now()

      const newState = {
        stock: stockData,
        sealedStock: sealedStockData,
        fetchedAt,
        fetchedAtTimestamp,
      }

      setFetchState(InventoryFetchState.IDLE)
      setInventoryState(newState)

      await Promise.all(
        Object.entries(otherGames || {}).map(([idGame, gameStock]) =>
          localDB.setItem<RawStockState>(rawStockKey(Number(idGame) as IdGame), {
            ...gameStock,
            fetchedAt,
            fetchedAtTimestamp,
          })
        )
      )
    } catch (error: any) {
      handle(error)
      if (error?.response?.data?.error?.message === 'missingCardmarketConnection') {
        setUser((user) => ({ ...user!, cardmarketUsername: '' }))
        setFetchState(InventoryFetchState.FAILED_DISCONNECTED_ACCOUNT)
        throw new Error('missingCardmarketConnection')
      } else {
        setFetchState(InventoryFetchState.FAILED_UNKNOWN)
      }
    }
    setFetchingDialogOpen(false)
  }, [user?.cardmarketUsername, activeGame.idGame, fetchUserStock])

  const someArticleEdited = Boolean(
    inventoryState.stock.find((article) => isArticleEdited(article))
  )

  const [confirmFetchStock, ConfirmFetchStockDialog] = useConfirm(
    t('stockPricing.areYouSureRefresh'),
    '',
    () => fetchStock()
  )

  const onFetchStock = useCallback(async () => {
    if (someArticleEdited) {
      confirmFetchStock()
    } else {
      fetchStock()
    }
  }, [someArticleEdited, fetchStock])

  const determineTab = useCallback((article: InventoryArticle): StockTab => {
    if (article.failedAutopricing) return 'failedAutopricing'
    else if (article.isEdited()) return 'edited'
    else return 'unedited'
  }, [])

  const splitStockByTab = useCallback(
    (stock: InventoryArticle[]) => {
      const stockByTab: Record<StockTab, InventoryArticle[]> = {
        unedited: [],
        edited: [],
        failedAutopricing: [],
      }

      stock.forEach((article) => {
        stockByTab[determineTab(article)].push(article)
      })

      return stockByTab
    },
    [determineTab]
  )
  if (user?.subscription.allFeaturesDisabled || user?.featureFlags?.banned) {
    return <Redirect to="/" />
  }

  const refreshStockAction = useMemo(
    () => ({
      key: 'get-stock-btn',
      fn: onFetchStock,
      Icon: RefreshStockIcon,
      actionLabel: t('stockPricing.actions.refreshStock'),
      fetchedAtTimestamp: inventoryState.fetchedAtTimestamp,
      tooltip: (fetchedAt: string) => (
        <>
          <Typography variant="body2">
            {t('stockPricing.stockFetchedOn')} <b>{fetchedAt}</b>
          </Typography>
          <Typography variant="body2">
            <b>{t('stockPricing.clickToRefresh')}</b>
          </Typography>
        </>
      ),
    }),
    [onFetchStock, inventoryState.fetchedAtTimestamp]
  )

  const extraDockActions = [
    {
      key: 'singles-stock-btn',
      Icon: SinglesStockIcon,
      actionLabel: t('stockPricing.actions.singlesStock'),
      fn: () => setActiveStockType(STOCK_TYPES.SINGLES),
    },
    {
      key: 'sealed-stock-btn',
      Icon: SealedIcon,
      actionLabel: t('stockPricing.actions.sealedStock'),
      fn: () => setActiveStockType(STOCK_TYPES.SEALED),
    },
  ]

  const openPublishDialog = useCallback(() => {
    setPublishChangesDialogOpen(true)
  }, [setPublishChangesDialogOpen])

  const emptyStockText = {
    [InventoryFetchState.FETCHING]: t('stockPricing.loadingStock'),
    [InventoryFetchState.IDLE]: t('stockPricing.noStock'),
    [InventoryFetchState.FAILED_DISCONNECTED_ACCOUNT]: t('error.fetchStock'),
    [InventoryFetchState.FAILED_UNKNOWN]: t('error.fetchStockUnknown'),
  }[fetchState]

  const handleChangesPublished = ({ failures, successfulIds }: PublishChangesResult) => {
    if (failures && failures.length > 0) {
      setPublishFailures(failures)
      setPublishErrorsDialogOpen(true)
    }

    // Update local state: apply changes for successfully published articles
    // Use string Set to avoid type mismatch (idArticle can be number or string)
    const successSet = new Set(successfulIds.map(String))
    const applyAndFilter = (articles: InventoryArticle[]) =>
      articles
        .map((article) => {
          if (successSet.has(String(article.idArticle)) && article.isEdited()) {
            return createInventoryArticle(article.applyChanges().toPlainObject())
          }
          return article
        })
        .filter((article) => article.quantity > 0)

    setInventoryState((prev) => ({
      ...prev,
      stock: applyAndFilter(prev.stock),
      sealedStock: applyAndFilter(prev.sealedStock),
    }))
  }

  return (
    <>
      <InventoryPresenter
        tabs={tabs}
        determineTab={determineTab}
        splitStockByTab={splitStockByTab}
        emptyStockText={emptyStockText}
        stockState={inventoryState}
        setStockState={setInventoryState}
        refreshStockAction={refreshStockAction}
        extraDockActions={extraDockActions}
        openPublishDialog={openPublishDialog}
      />
      <FetchingDialog
        title={t('stockPricing.loadingStock')}
        content={
          <>
            <Box marginTop={3} height="8rem" width="fit-content" margin="0 auto">
              <CircularProgress size={100} />
            </Box>
            <Box marginTop={2} width="fit-content">
              <Typography variant="body1">{t('stockPricing.loadingStockSlow')}</Typography>
            </Box>
          </>
        }
      />
      <ConfirmFetchStockDialog />
      {publishChangesDialogOpen && (
        <PublishChangesDialog
          articles={
            activeStockType === 'singles'
              ? inventoryState.stock.filter((item) => item.isEdited() && item.idArticle)
              : inventoryState.sealedStock.filter((item) => item.isEdited() && item.idArticle)
          }
          handleChangesPublished={handleChangesPublished}
          open={publishChangesDialogOpen}
          setOpen={setPublishChangesDialogOpen}
        />
      )}
      <PublishErrorsDialog
        title={t('publish.error')}
        content={
          <Box>
            {publishFailures?.map((f) => (
              <>
                <LocalArticle article={f.article} />
                {f.attributeError}
              </>
            ))}
          </Box>
        }
      />
    </>
  )
}
